import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { baseUrl, formatDate, getDateWithDay } from "../../comman/config";
import Loader from '../../components/Loader';
import { isFilterActive, matchFilterText, patchFilter } from "../../components/Filter/columnFilter";
import ColumnFilterRow from "../../components/Filter/ColumnFilterRow";
import { Link } from 'react-router-dom';
import { menuAboveTable } from "../../comman/selectMenu";

function ActualStockReport() {
    const [stockData, setStockData] = useState([]);
    // Inline per-column filters: { [colKey]: { op, v1, v2 } }, matched against the cell's own text.
    // Stock Qty. renders as a link, so its accessor returns the number inside it, not the markup.
    const [filters, setFilters] = useState({});
    const setColFilter = (key, patch) => setFilters((prev) => patchFilter(prev, key, patch));
    const FILTER_COLS = [
        { key: 'category',   text: (r) => r.category_name },
        { key: 'ingredient', text: (r) => `${r.ingredient_name} ${r.eng_name ? `(${r.eng_name})` : ''}`.trim() },
        { key: 'quantity',   text: (r) => r.quantity },
        { key: 'unit',       text: (r) => r.unit },
        { key: 'updated',    text: (r) => getTimeSinceUpdate(r.last_updated) },
    ];
    const [loading, setLoading] = useState(false);

    // State for filters
    const [eventList, setEventList] = useState([]);
    const [ingredientList, setIngredientList] = useState([]);
    const [categoryList, setCategoryList] = useState([]);
    const [storeList, setStoreList] = useState([]);

    // Selected filter values
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedIngredients, setSelectedIngredients] = useState([{ value: 'all', label: 'All' }]);
    const [selectedCategories, setSelectedCategories] = useState([{ value: 'all', label: 'All' }]);
    const [selectedStores, setSelectedStores] = useState([]);

    // Popup state
    const [showPopup, setShowPopup] = useState(false);
    const [currentIngredient, setCurrentIngredient] = useState(null);
    const [entryHistory, setEntryHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    useEffect(() => {
        // Load dropdown data when component mounts
        getEventList();
        getIngredientList();
        getCategoryList();
        getStoreList();
    }, []);

    const getEventList = async () => {
        try {
            const response = await axios.get(`${baseUrl}/comman/list?table=event_master&condition=status=1`);
            setEventList(response.data.data);
        } catch (error) {
            console.error("Error fetching event list:", error);
        }
    };

    const getIngredientList = async () => {
        try {
            const response = await axios.get(`${baseUrl}/ingredients/basic?condition=status=1`);
            setIngredientList(response.data.data);
        } catch (error) {
            console.error("Error fetching ingredient list:", error);
        }
    };

    const getCategoryList = async () => {
        try {
            const response = await axios.get(`${baseUrl}/ingredients/category/list?condition=status=1`);
            setCategoryList(response.data.data);
        } catch (error) {
            console.error("Error fetching category list:", error);
        }
    };

    const getStoreList = async () => {
        try {
            const response = await axios.get(`${baseUrl}/comman/list?table=store&condition=status=1`);
            const data = response.data.data;
            const newObject = { id: 'all', name: 'All' };
            data.unshift(newObject);
            setStoreList(data);
            setSelectedStores([{ value: 1, label: 'Main Store' }]);
        } catch (error) {
            console.error("Error fetching store list:", error);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const isAllSelected = (list) => list.some(item => item.value === 'all');

            const params = {
                event: selectedEvent ? selectedEvent.value : null
            };
    
            if (!isAllSelected(selectedIngredients)) {
                params.ingredients = JSON.stringify(selectedIngredients.map(i => i.value));
            }
    
            if (!isAllSelected(selectedCategories)) {
                params.categories = JSON.stringify(selectedCategories.map(c => c.value));
            }
    
            if (!isAllSelected(selectedStores)) {
                params.stores = JSON.stringify(selectedStores.map(s => s.value));
            }

            const response = await axios.get(`${baseUrl}/stock_report/list`, {params});
            setStockData(response.data.data);
        } catch (error) {
            console.error("Error fetching stock report:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        // The inline per-column filters go too. Leaving them set would look like Clear had
        // emptied the report, when a column filter is still hiding the rows.
        setFilters({});
        setSelectedEvent(null);
        setSelectedIngredients([]);
        setSelectedCategories([]);
        setSelectedStores([]);
    };

    const fetchIngredientHistory = async (ingredientId) => {
        setLoadingHistory(true);
        try {
            const response = await axios.get(`${baseUrl}/stock_report/last_entries`, {
                params: {
                    ingredientId,
                    limit: 10
                }
            });
            setEntryHistory(response.data.data);
        } catch (error) {
            console.error("Error fetching ingredient history:", error);
            setEntryHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleStockClick = (ingredient) => {
        setCurrentIngredient(ingredient);
        fetchIngredientHistory(ingredient.ingredient_id);
        setShowPopup(true);
    };

    const closePopup = () => {
        setShowPopup(false);
        setCurrentIngredient(null);
        setEntryHistory([]);
    };

    // Calculate days since last update
    // const getDaysSinceUpdate = (lastUpdateDate) => {
    //     const today = new Date();
    //     const lastUpdate = new Date(lastUpdateDate);
    //     const diffTime = Math.abs(today - lastUpdate);
    //     return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    // };

    const getTimeSinceUpdate = (lastUpdateDate) => {
        const now = new Date();
        const lastUpdate = new Date(lastUpdateDate);
        const diffMs = now - lastUpdate;
    
        const diffHours = diffMs / (1000 * 60 * 60);
    
        if (diffHours >= 24) {
            const diffDays = Math.floor(diffHours / 24);
            return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
        } else {
            const roundedHours = Math.floor(diffHours);
            return `${roundedHours} hour${roundedHours !== 1 ? 's' : ''}`;
        }
    };

    // Derived here rather than beside FILTER_COLS: the "Updated since" accessor calls
    // getTimeSinceUpdate above, which is a const and would still be in its temporal dead zone.
    const activeFilterCols = FILTER_COLS.filter((c) => isFilterActive(filters[c.key]));
    const visibleRows = stockData.filter((r) => activeFilterCols.every((c) => matchFilterText(c.text(r), filters[c.key])));
    

    return (
        <div className="p-4">
            {loading && <Loader />}

            <div className="flex justify-between items-center py-4 px-6">
                <h1 className="text-2xl font-bold">Actual Stock Report</h1>
                <div>
                    <Link to="/">
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">
                            Back
                        </button>
                    </Link>
                </div>
            </div>

            {/* Filter Section */}
            <div className="bg-white p-4 mb-6 shadow-md rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    {/* <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Event List</label>
                        <Select
                            {...menuAboveTable}
                            className="basic-single"
                            classNamePrefix="select"
                            value={selectedEvent}
                            onChange={setSelectedEvent}
                            options={eventList.map(event => ({
                                value: event.id,
                                label: event.event_name
                            }))}
                            placeholder="Select Event"
                            isClearable
                        />
                    </div> */}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Ingredient List</label>
                        <Select
                            {...menuAboveTable}
                            isMulti
                            className="basic-multi-select"
                            classNamePrefix="select"
                            value={selectedIngredients}
                            onChange={setSelectedIngredients}
                            options={ingredientList.map(ingredient => ({
                                value: ingredient.id,
                                label: `${ingredient.ingredient} ${ingredient.eng_name ? `(${ingredient.eng_name})` : ''}`
                            }))}
                            placeholder="Select Ingredients"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Ingredient Category</label>
                        <Select
                            {...menuAboveTable}
                            isMulti
                            className="basic-multi-select"
                            classNamePrefix="select"
                            value={selectedCategories}
                            onChange={setSelectedCategories}
                            options={categoryList.map(category => ({
                                value: category.id,
                                label: category.name
                            }))}
                            placeholder="Select Categories"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Store</label>
                        <Select
                            {...menuAboveTable}
                            isMulti
                            className="basic-multi-select"
                            classNamePrefix="select"
                            value={selectedStores}
                            onChange={setSelectedStores}
                            options={storeList.map(store => ({
                                value: store.id,
                                label: store.name
                            }))}
                            placeholder="Select Stores"
                        />
                    </div>
                </div>

                <div className="flex items-end gap-2">
                    <button
                        onClick={handleSubmit}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200 disabled:bg-blue-300"
                    >
                        Submit
                    </button>
                    <button
                        onClick={handleClear}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200 disabled:bg-blue-300"
                    >
                        Clear
                    </button>
                </div>
            </div>

            {/* Report Table */}
            <div className="relative shadow-md sm:rounded-lg">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-gray-500 border-collapse border border-gray-300">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                            <tr>
                                <th scope="col" className="px-4 py-3 border border-gray-300 text-center">Ingredient Category</th>
                                <th scope="col" className="px-4 py-3 border border-gray-300 text-center">Ingredient Name</th>
                                <th scope="col" className="px-4 py-3 border border-gray-300 text-center">Stock Qty.</th>
                                <th scope="col" className="px-4 py-3 border border-gray-300 text-center">Unit</th>
                                <th scope="col" className="px-4 py-3 border border-gray-300 text-center">Updated since</th>
                            </tr>
                            {/* Inline per-column filter row (contains / = / ≤ / ≥ / between / not between) */}
                            <ColumnFilterRow
                                thClassName="px-1 py-1 border border-gray-300 bg-gray-50 align-top"
                                cols={FILTER_COLS.map((c) => ({ key: c.key }))}
                                filters={filters}
                                onChange={setColFilter} />
                        </thead>
                        <tbody>
                            {visibleRows.length > 0 ? (
                                visibleRows.map((item, index) => (
                                    <tr key={index} className="bg-white border-b">
                                        <td className="px-4 py-3 border border-gray-300 text-center">{item.category_name}</td>
                                        <td className="px-4 py-3 border border-gray-300 text-center">
                                            {item.ingredient_name} {item.eng_name ? `(${item.eng_name})` : ''}
                                        </td>
                                        <td
                                            className="px-4 py-3 border border-gray-300 text-center text-blue-600 underline cursor-pointer hover:text-blue-800"
                                            onClick={() => handleStockClick(item)}
                                        >
                                            <span className="hover:font-bold">{item.quantity}</span>
                                        </td>
                                        <td className="px-4 py-3 border border-gray-300 text-center">{item.unit}</td>
                                        <td className="px-4 py-3 border border-gray-300 text-center">
                                            {getTimeSinceUpdate(item.last_updated)}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-4 py-3 text-center border">
                                        No data available. Please adjust your filter criteria.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Popup for entry history */}
            {showPopup && currentIngredient && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg w-4/5 max-w-4xl max-h-screen overflow-hidden">
                        <div className="p-4 border-b flex justify-between items-center bg-gray-100">
                            <h3 className="text-lg font-semibold">
                                Last 10 entries
                            </h3>
                            <button
                                onClick={closePopup}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="p-4">
                            <p className="mb-4">
                                <strong>Name:</strong> {currentIngredient.ingredient_name}
                            </p>

                            {loadingHistory ? (
                                <div className="flex justify-center items-center p-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                </div>
                            ) : (
                                <table className="w-full border-collapse border border-gray-300">
                                    <thead>
                                        <tr>
                                            <th className="border border-gray-300 px-4 py-2 text-center">Date</th>
                                            <th className="border border-gray-300 px-4 py-2 text-center">Time stamp</th>
                                            <th className="border border-gray-300 px-4 py-2 text-center">Qty.</th>
                                            <th className="border border-gray-300 px-4 py-2 text-center">Unit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {entryHistory.map((entry, idx) => (
                                            <tr key={idx}>
                                                <td className="border border-gray-300 px-4 py-2 text-center">
                                                {getDateWithDay(entry.entry_date)}
                                                </td>
                                                <td className="border border-gray-300 px-4 py-2 text-center">{entry.time_stamp}</td>
                                                <td className="border border-gray-300 px-4 py-2 text-center">{entry.quantity}</td>
                                                <td className="border border-gray-300 px-4 py-2 text-center">{entry.unit_name}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div className="p-4 border-t flex justify-end">
                            <button
                                onClick={closePopup}
                                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200 disabled:bg-blue-300"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ActualStockReport;